import { Button, MuiAlert, useTempDataContext } from '@fox/atoms';
import { FlexColumnSpaced, FlexRowSpacedRightAligned } from '@fox/layout';
import { CardElement, Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useImperativeHandle, useState } from 'react';
import { DESIGN_MODE_INTENT } from '../../../constants';
import { PaymentAddComponentProps } from '../../../contracts';

function StripeInnerForm({
	clientSecret,
	onSuccess,
	completeIntent,
	intentData,
	showSave,
	onCancel
}: PaymentAddComponentProps & { clientSecret: string }) {
	const stripe = useStripe();
	const [cardEl, setCardElement] = useState(undefined);
	const [error, setError] = useState<string | undefined>();

	const onSubmit = async event => {
		// We don't want to let default form submission happen here,
		// which would refresh the page.
		event?.preventDefault();
		if (!stripe || !cardEl) {
			// Stripe.js has not yet loaded.
			// Make sure to disable form submission until Stripe.js has loaded.
			return;
		}
		const res = await stripe.confirmCardSetup(clientSecret, {
			payment_method: {
				card: cardEl
			}
		});

		if (res.error) {
			console.log('Failed to confirm', res);
			setError(res.error.message);
			throw new Error('Failed to confirm card details');
		}
		const acres = await completeIntent(
			intentData.intentId,
			JSON.stringify({
				paymentMethodId:
					typeof res.setupIntent.payment_method === 'string' ? res.setupIntent.payment_method : res.setupIntent.payment_method.id
			})
		);

		// We assume success if we get to this point
		if (tempData) tempData.cardFormDataValid = true;
		//We need to clear error - we may have gotten a successful card after a failed card
		//without a component reload
		setError(undefined);

		onSuccess(acres);
	};
	const tempData = useTempDataContext();

	useImperativeHandle(
		r => {
			if (tempData) tempData.submitFunc = r;
		},
		() => ({
			submit: onSubmit
		})
	);

	return (
		<form onSubmit={onSubmit}>
			<FlexColumnSpaced>
				<CardElement
					onReady={el => setCardElement(el)}
					onChange={e => {
						console.log('stripe on change ', e);
						setError(e.error?.message);

						if (tempData) {
							tempData.cardFormDataValid = e.complete;
						}
					}}
					options={{ hidePostalCode: true }}
				/>
				{error ? (
					<MuiAlert severity="error" color="error">
						{error}
					</MuiAlert>
				) : null}

				<FlexRowSpacedRightAligned>
					{onCancel ? (
						<Button variant="secondary" onClick={() => onCancel()}>
							Cancel
						</Button>
					) : null}
					{showSave ? (
						<Button variant="primary" type="submit" disabled={!stripe || !cardEl || !!error}>
							Save
						</Button>
					) : null}
				</FlexRowSpacedRightAligned>
			</FlexColumnSpaced>
		</form>
	);
}

type StripeFormWrapperProps = PaymentAddComponentProps & {
	pubKey: string;
};

function StripeFormWrapper({ pubKey, ...rest }: StripeFormWrapperProps) {
	const clientSecret = rest.intentData?.key;
	const [stripePromise] = useState(() => loadStripe(pubKey));
	const options = {
		clientSecret
	};
	if (!clientSecret && rest.intentData?.error) {
		return null;
	}
	if (clientSecret === DESIGN_MODE_INTENT.key) {
		return <MuiAlert severity="info">Stripe Payment</MuiAlert>;
	}
	return (
		<Elements stripe={stripePromise} options={options}>
			<StripeInnerForm clientSecret={clientSecret} {...rest} />
		</Elements>
	);
}

export function StripeAddCreditCard(props: PaymentAddComponentProps) {
	var publishableKey = props.settings?.['publishableKey'];

	return <StripeFormWrapper pubKey={publishableKey} {...props} />;
}
